/**
 * EnhancedRulers.tsx
 * Règles améliorées avec unités et meilleur feedback visuel (style Figma/Photoshop)
 * UX: Rulers avec unités (px/mm/cm), indicateur de position précis
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import Konva from 'konva';
import { useAppStore } from '../../../services/StateService';

interface EnhancedRulersProps {
  canvasWidth: number;
  canvasHeight: number;
  zoomLevel: number;
  canvasPosition: { x: number; y: number };
  stageRef: React.RefObject<Konva.Stage | null>;
}

export const EnhancedRulers: React.FC<EnhancedRulersProps> = ({
  canvasWidth,
  canvasHeight,
  zoomLevel,
  canvasPosition,
  stageRef,
}) => {
  const horizontalRulerRef = useRef<HTMLDivElement>(null);
  const verticalRulerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const [showRulers, _setShowRulers] = useState(true);
  const dpi = useAppStore(state => state.project.dpi);
  // P3 : PhysicalFormat n'a pas d'`unit` standard ; valeur dynamique tolérée.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const unit = useAppStore(state => (state.project.format as any).unit || 'px');

  // Convertir pixels en unité sélectionnée
  const convertToUnit = useMemo(() => {
    return (px: number): number => {
      if (unit === 'px') return px;
      if (unit === 'mm') return (px / dpi) * 25.4;
      if (unit === 'cm') return (px / dpi) * 2.54;
      if (unit === 'inch') return px / dpi;
      return px;
    };
  }, [unit, dpi]);

  // Formater la valeur selon l'unité
  const formatValue = (px: number): string => {
    const value = convertToUnit(px);
    if (unit === 'px') return `${Math.round(value)}px`;
    if (unit === 'mm') return `${value.toFixed(1)}mm`;
    if (unit === 'cm') return `${value.toFixed(2)}cm`;
    if (unit === 'inch') return `${value.toFixed(2)}"`;
    return `${Math.round(value)}px`;
  };

  useEffect(() => {
    const handleMouseMove = (_e: MouseEvent) => {
      if (!stageRef.current) return;

      const stage = stageRef.current;
      const container = stage.container();
      if (!container) return;

      const _rect = container.getBoundingClientRect();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      // Position relative au canvas (en tenant compte du zoom et pan)
      const stagePos = stage.position();
      const scale = stage.scaleX();
      const x = (pointer.x - stagePos.x) / scale;
      const y = (pointer.y - stagePos.y) / scale;

      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [stageRef, zoomLevel]);

  // Générer les graduations adaptatives selon le zoom
  const generateGraduations = (size: number, _isHorizontal: boolean) => {
    const graduations = [];
    // Ajuster le pas selon le zoom et l'unité
    let step = 50;
    if (unit === 'mm') step = 5;
    else if (unit === 'cm') step = 1;
    else if (unit === 'inch') step = 0.5;
    else step = zoomLevel >= 1 ? 50 : zoomLevel >= 0.5 ? 100 : 200;

    // Convertir en pixels pour le calcul
    const stepPx = unit === 'px' ? step : (step / 25.4) * dpi;

    for (let i = 0; i <= size; i += stepPx) {
      const _value = convertToUnit(i);
      graduations.push({
        position: i,
        label: formatValue(i),
        isMajor: i % (stepPx * 5) === 0,
        isMedium: i % (stepPx * 2) === 0,
      });
    }

    return graduations;
  };

  const horizontalGraduations = generateGraduations(canvasWidth, true);
  const verticalGraduations = generateGraduations(canvasHeight, false);

  if (!showRulers) return null;

  return (
    <div className="enhanced-rulers">
      {/* Règle horizontale */}
      <div ref={horizontalRulerRef} className="ruler-horizontal-enhanced">
        <div className="ruler-unit-label">{unit}</div>
        {horizontalGraduations.map(grad => (
          <div
            key={grad.position}
            className={`ruler-graduation ${grad.isMajor ? 'major' : grad.isMedium ? 'medium' : 'minor'}`}
            style={{
              left: `${grad.position * zoomLevel + canvasPosition.x}px`,
            }}
          >
            {grad.isMajor && <span className="ruler-label">{grad.label}</span>}
            <div className="ruler-tick" />
          </div>
        ))}
        {/* Indicateur de position du curseur */}
        {mousePosition && (
          <>
            <div
              className="ruler-cursor-indicator"
              style={{
                left: `${mousePosition.x * zoomLevel + canvasPosition.x}px`,
              }}
            />
            <div
              className="ruler-cursor-label"
              style={{
                left: `${mousePosition.x * zoomLevel + canvasPosition.x}px`,
              }}
            >
              {formatValue(mousePosition.x)}
            </div>
          </>
        )}
      </div>

      {/* Règle verticale */}
      <div ref={verticalRulerRef} className="ruler-vertical-enhanced">
        <div className="ruler-unit-label">{unit}</div>
        {verticalGraduations.map(grad => (
          <div
            key={grad.position}
            className={`ruler-graduation ${grad.isMajor ? 'major' : grad.isMedium ? 'medium' : 'minor'}`}
            style={{
              top: `${grad.position * zoomLevel + canvasPosition.y}px`,
            }}
          >
            {grad.isMajor && <span className="ruler-label">{grad.label}</span>}
            <div className="ruler-tick" />
          </div>
        ))}
        {/* Indicateur de position du curseur */}
        {mousePosition && (
          <>
            <div
              className="ruler-cursor-indicator"
              style={{
                top: `${mousePosition.y * zoomLevel + canvasPosition.y}px`,
              }}
            />
            <div
              className="ruler-cursor-label"
              style={{
                top: `${mousePosition.y * zoomLevel + canvasPosition.y}px`,
              }}
            >
              {formatValue(mousePosition.y)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
